import { QueryClient } from '@tanstack/react-query';
import produce from 'immer';
import { unescape } from 'underscore';

import { ActivityResponse } from '~teacher/hook';
import { queryKey } from '~teacher/hook/activity/queryKey';
import { Student } from '~teacher/models/activities';

import { findStudentIdByName, findStudentIdByUuid } from '../../utils';

export const updateStudentNameSet = produce(
  (
    activityData: ActivityResponse,
    userUuid: string,
    studentName: string,
    isRosteredRoom: boolean,
    userPreviousUuid?: string
  ) => {
    if (userPreviousUuid) {
      // Remove previous attempt
      const studentId = findStudentIdByUuid(activityData, userPreviousUuid);
      delete activityData.students[studentId];

      // Add new attempt
      const newStudent: Student = {
        id: Date.now(), // tempId
        isFinished: false,
        isPresent: true,
        name: unescape(studentName),
        userUuid: userUuid,
      };
      activityData.students[newStudent.id] = newStudent;
      return;
    }

    if (isRosteredRoom) {
      // In a rostered room, names are unique and we must use the name to correct the rostered student's uuid
      const studentId = findStudentIdByName(activityData, studentName);
      if (studentId) {
        activityData.students[studentId].userUuid = userUuid;
        activityData.students[studentId].isPresent = true;
      }
    } else {
      const studentId = findStudentIdByUuid(activityData, userUuid);
      if (studentId) {
        activityData.students[studentId].name = unescape(studentName);
      } else {
        // If student doesn't exist yet, add a temporary student
        const newStudent: Student = {
          id: Date.now(), // tempId
          isFinished: false,
          isPresent: true,
          name: unescape(studentName),
          userUuid: userUuid,
        };
        activityData.students[newStudent.id] = newStudent;
      }
    }
  }
);

export const studentNameSetCacheUpdater = ({
  queryClient,
  activityInstanceId,
  userUuid,
  userPreviousUuid,
  studentName,
  isRosteredRoom,
}: {
  queryClient: QueryClient;
  activityInstanceId: number;
  userUuid: string;
  userPreviousUuid?: string;
  studentName: string;
  isRosteredRoom: boolean;
}) => {
  queryClient.setQueryData(
    queryKey.byId(activityInstanceId),
    (activityData: ActivityResponse) =>
      updateStudentNameSet(
        activityData,
        userUuid,
        studentName,
        isRosteredRoom,
        userPreviousUuid
      )
  );
};
